import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { coreFeatureService } from '../services/coreFeatureService';
import { blobticRealtimeService } from '../services/blobticRealtimeService';
import { useAuth } from './AuthContext';
import { useSystemParameters } from './SystemParametersContext';

interface ChatContextType {
    unreadCount: number;
    unreadCounts: { [key: number]: number };
    refreshUnreadCount: () => Promise<void>;
    markAsRead: (senderId: number) => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const { getParam } = useSystemParameters();
    const [unreadCounts, setUnreadCounts] = useState<{ [key: number]: number }>({});
    const [unreadCount, setUnreadCount] = useState(0);

    const refreshUnreadCount = useCallback(async () => {
        if (!user) {
            setUnreadCounts({});
            setUnreadCount(0);
            return;
        }
        try {
            const counts = await coreFeatureService.getUnreadChatCounts(user.id);
            setUnreadCounts(counts);
            // Calculate total from map values
            const total = Object.values(counts).reduce((a, b) => a + b, 0);
            setUnreadCount(total);
        } catch (e) {
            console.error("Failed to fetch unread chat count", e);
        }
    }, [user]);

    const markAsRead = useCallback(async (senderId: number) => {
        if (!user) return;
        // Optimistic update
        setUnreadCounts(prev => {
            const next = { ...prev };
            delete next[senderId];
            return next;
        });

        // Backend call
        await coreFeatureService.markChatAsRead(senderId, user.id);
        await refreshUnreadCount(); // Refresh to be sure
    }, [user, refreshUnreadCount]);

    // Initial fetch, WebSockets real-time listener, and polling fallback
    useEffect(() => {
        if (user) {
            // Always load for first time
            refreshUnreadCount();

            // Real-time WebSocket listener for chat messages
            const unsubscribe = blobticRealtimeService.subscribe((event) => {
                const obj = (event.objectName || '').toLowerCase();
                if (obj === 'chat_message' || obj === 'sys_chat' || obj === 'tabp_chat_message') {
                    refreshUnreadCount();
                }
            });

            // Read timer parameter in seconds (0 = disabled)
            const timerSecStr = getParam('Sync_Notifications_Timer_Seconds', '0');
            const timerSec = parseInt(timerSecStr, 10);
            let interval: any = null;

            if (!isNaN(timerSec) && timerSec > 0) {
                interval = setInterval(refreshUnreadCount, timerSec * 1000);
            }

            return () => {
                unsubscribe();
                if (interval) clearInterval(interval);
            };
        }
    }, [user, refreshUnreadCount, getParam]);

    return (
        <ChatContext.Provider value={{ unreadCount, unreadCounts, refreshUnreadCount, markAsRead }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = (): ChatContextType => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};
